import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { Check, X, ChevronDown, Star, Shield, Eye, Moon, Sparkles, Heart, Leaf } from 'lucide-react';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const DeepSleep = () => {
  const location = useLocation();
  
  // Get current URL query parameters and append them to destination URLs
  const getUrlWithParams = (baseUrl) => {
    const currentParams = location.search;
    if (!currentParams) return baseUrl;
    
    const separator = baseUrl.includes('?') ? '&' : '?';
    const paramsToAppend = currentParams.startsWith('?') ? currentParams.slice(1) : currentParams;
    
    return `${baseUrl}${separator}${paramsToAppend}`;
  };

  const productUrl = useMemo(() => 
    getUrlWithParams("https://kickinon.com/products/self-heating-steam-eye-mask"),
    [location.search]
  );

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 21,
    minutes: 9,
    seconds: 31
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        let { days, hours, minutes, seconds } = prevTime;

        if (seconds > 0) {
          seconds--;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes--;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours--;
            } else {
              hours = 23;
              if (days > 0) {
                days--;
              }
            }
          }
        }

        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => String(num).padStart(2, '0');

  const faqData = [
    {
      question: "How long does each Kickin'On Steam Eye Mask last?",
      answer: "Each Kickin'On mask provides a consistent 20–25 minutes of gentle, warming steam. It's a great window to unwind, whether you're in bed, on the couch, or trying to relax on a flight."
    },
    {
      question: "Can I use it every night?",
      answer: "Yes. Kickin'On is designed to be a cosy nightly ritual. Many people use one most evenings and keep a spare pack in their travel bag for flights and hotel nights."
    },
    {
      question: "What does it feel like when it's on?",
      answer: "Most people describe it as a soft, lightly weighted hug for their eyes. The mask warms up on its own, the world goes dark, and your face finally gets a moment to relax."
    },
    {
      question: "Is it travel friendly?",
      answer: "Absolutely. Each mask is single-use, cordless and compact, making it perfect for carry-ons, overnight bags and long-haul flights. No liquids, no batteries, no fuss."
    },
    {
      question: "Do I need to change anything else in my routine?",
      answer: "No. You don't have to overhaul your life to enjoy it. Just add Kickin'On as a small, warm cue at the end of your day — or at the start of a long flight — and let the warmth and darkness do the rest."
    }
  ];

  const reviews = [
    {
      author: "Sarah M.",
      text: "I used to lie in bed scrolling because I couldn't switch off. Now I put this on, feel my eyes relax, and I drift off so much easier. My mornings feel a lot less brutal."
    },
    {
      author: "Jessica L.",
      text: "It's like a tiny spa moment for my face. The warmth, the darkness, the quiet… I finally feel my whole body unclench. I wake up feeling way more rested than I expect based on my sleep."
    },
    {
      author: "Amanda K.",
      text: "This is my 'day is officially over' moment. Ten to fifteen minutes with the mask on and my brain finally gets the memo to slow down."
    },
    {
      author: "Michelle R.",
      text: "A lifesaver for flights. I use one after takeoff and actually arrive feeling semi-human instead of wrecked. It lives in my carry-on now."
    },
    {
      author: "Lauren T.",
      text: "Finally a ritual I've stuck with. I use Kickin'On most nights and before big days. My evenings feel calmer and my mornings less chaotic."
    },
    {
      author: "Rachel W.",
      text: "I keep a box in my nightstand and another in my work bag. On huge days I'll use one in the afternoon and one at night. It's my reset button."
    }
  ];

  const timelineData = [
    {
      period: "Week 1–2: Your new wind-down cue",
      description: "You start linking the warm steam and darkness with \"time to slow down.\" Evenings and travel days begin to feel a little more contained."
    },
    {
      period: "Week 3–4: Evenings feel more intentional",
      description: "Using your mask becomes a familiar signal that the busy part of the day is over. Nights feel a bit less chaotic and more like a soft landing."
    },
    {
      period: "Month 2: Calm becomes part of the routine",
      description: "Your evening and travel steam ritual starts to feel automatic. You know exactly how you'll close out the day, wherever you are."
    },
    {
      period: "Month 3: A ritual you protect like your coffee",
      description: "You look forward to your 10–15 minutes of warm darkness. Bedtime stops feeling like a battle and becomes a moment you actually protect."
    }
  ];

  const comparisonFeatures = [
    "Self-heating, gentle steam for a cosy wind-down",
    "Creates a simple \"end of day\" cue you can repeat anywhere",
    "Single-use, hygienic & travel-ready",
    "Even warmth for 20+ minutes — no cords, no microwave",
    "Designed as a nightly & travel wind-down ritual"
  ];

  const benefits = [
    "Gentle steam for a quick, cosy wind-down",
    "Feels soft and comforting around the eyes",
    "Supports a calmer evening wind-down routine",
    "Fresh, single-use & hygienic",
    "Cord-free, travel-friendly & ready anywhere"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Helmet>
        <title>
          The Tiny Japanese-Inspired Ritual Helping You Switch Off — Even 36,000 Feet in the Air
        </title>
        <meta
          name="description"
          content="Discover the cosy, self-heating steam eye mask ritual inspired by Japan that helps people wind down at night, on flights, and in hotel rooms — and feel a little more at ease."
        />
        <meta
          name="keywords"
          content="steam eye mask, wind-down ritual, travel comfort, Japanese eye mask, bedtime routine, relax before sleep, flight comfort"
        />
        <meta
          property="og:title"
          content="The Tiny Japanese-Inspired Ritual Helping You Switch Off — Even 36,000 Feet in the Air"
        />
        <meta
          property="og:description"
          content="A cosy self-heating steam mask that turns flights, hotel rooms, and busy nights into a small pocket of calm at the end of the day."
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="The Tiny Japanese-Inspired Ritual Helping You Switch Off — Even 36,000 Feet in the Air"
        />
        <meta
          name="twitter:description"
          content="See why people are adding this Japanese-inspired steam ritual to their travel bags and nightstands."
        />
        <link
          rel="canonical"
          href="https://kickinon.com/pages/switch-off-ritual"
        />
      </Helmet>

      {/* Sticky Top Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-green-800 to-green-700 shadow-lg">
        <div className="flex items-center justify-center h-14 px-4">
          <Badge variant="secondary" className="bg-green-600/20 text-white border-green-500/30 text-sm font-semibold">
            ⭐ Trending Nighttime Spa Treatment To Wake Up Refreshed
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto pt-20 px-4 sm:px-6 lg:px-8">
        
        {/* Hero Section */}
        <section className="text-center py-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-green-900 leading-tight mb-6">
            The tiny Japanese-inspired 10-min ritual helping people wake up feeling "refreshed"
          </h1>
          
          <p className="text-lg text-green-700/80 max-w-3xl mx-auto mb-8">
            The 7 reasons the relax occular reset helps those working in front of screens or traveling alot finally feel well rested.
          </p>
          
          <div className="flex items-center justify-center gap-4 text-sm text-green-600 mb-6">
            <span className="font-semibold">By Jennifer K.</span>
            <Separator orientation="vertical" className="h-4 bg-green-300" />
            <span>Last Updated December 2025</span>
          </div>
          
          <Card className="border-2 border-dashed border-amber-400 bg-amber-50 max-w-2xl mx-auto">
            <CardContent className="py-4">
              <p className="text-amber-700">
                <span className="font-bold text-amber-800">UPDATE:</span> Selling out faster than expected — Evening
                Ritual & Travel Bundles still available for a limited time.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Wide Hero Image */}
        <section className="mb-12">
          <div className="rounded-2xl overflow-hidden shadow-xl border border-green-200">
            <img
              src="/images/WO_Grid-1b.jpg"
              alt="Unwinding with Kickin'On steam eye mask"
              loading="lazy"
              decoding="async"
              className="w-full h-auto object-cover"
            />
          </div>
        </section>

        {/* Reason 1 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-square md:aspect-auto">
              <img
                src="/images/reason1.jpg"
                alt="Relaxing with steam eye mask before sleep"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                1. Relaxed eyes tell your brain "the day is finally done"
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  After a full day of screens, bright lights and to-do lists, your brain doesn't always get a clear "we're finished" signal.
                </p>
                <p>
                  Inspired by Japan's famous steam masks, Kickin'On wraps your eyes in gentle warmth and darkness — a cosy cue your mind can link with winding down.
                </p>
                <p>
                  For many people, that 10–15 minute ritual becomes the moment their body realises: <strong className="text-green-800">"We're off duty now. It's time to rest."</strong>
                </p>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Reason 2 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white order-2 md:order-1">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                2. Gentle warmth helps melt tension around tired eyes
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  When your eyes feel tight, strained or "held", it's harder for the rest of your face — and mind — to switch off.
                </p>
                <p>
                  The self-heating mask creates a soft, even warmth that many people find helps their eye area relax and their eyelids feel pleasantly heavier.
                </p>
                <p>
                  It's like bringing a tiny spa moment into bed, on the couch, or into seat 14B — without appointments, cords, or effort.
                </p>
              </div>
            </CardContent>
            <div className="aspect-square md:aspect-auto order-1 md:order-2">
              <img
                src="/images/reason6.jpg"
                alt="Spa-like eye relaxation at home or while travelling"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </Card>

        {/* Reason 3 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-square md:aspect-auto">
              <img
                src="/images/reason4.jpg"
                alt="Hygienic single-use steam eye mask"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                3. Steam helps comfort dry, screen-tired eyes before sleep
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  Long days staring at screens, air-conditioning and dry air can leave eyes feeling gritty and overworked by bedtime.
                </p>
                <p>
                  Each single-use Kickin'On mask gently warms to create soothing steam around the eye area — a cosy way to help comfort tired, dry-feeling eyes before you close them.
                </p>
                <p>
                  No microwaving towels or guessing temperatures — just consistent, gentle warmth that makes shutting your eyes feel good again.
                </p>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Reason 4 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white order-2 md:order-1">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                4. A tiny ritual that fits real, imperfect nights
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  You don't need the perfect routine or a spotless sleep schedule to enjoy deeper rest.
                </p>
                <p>
                  Open the packet, place the mask on, feel the warmth rise, and let the world go dark for 10–15 minutes — that's it.
                </p>
                <p>
                  Even on nights when your mind is still busy, this simple ritual can help you feel more settled, so drifting into sleep feels more possible.
                </p>
              </div>
            </CardContent>
            <div className="aspect-square md:aspect-auto order-1 md:order-2">
              <img
                src="/images/reason7.jpg"
                alt="Simple relaxing ritual with steam eye mask"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </Card>

        {/* Reason 5 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-square md:aspect-auto">
              <img
                src="/images/reason2.jpg"
                alt="Calm night routine with steam eye mask"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                5. Helps break the scroll → wired → can't-sleep loop
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  It's easy to end the night doom-scrolling, blinking through tired eyes and wondering why you still feel wired.
                </p>
                <p>
                  The moment the mask goes on, the screen goes off. Your world gets darker, quieter, and your eyes finally get permission to relax.
                </p>
                <p>
                  That simple shift away from bright screens into warm darkness can make easing into deep sleep feel a lot more natural.
                </p>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Reason 6 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white order-2 md:order-1">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                6. A softer landing at night = a gentler start in the morning
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  How you feel when the alarm goes off often starts with how you managed to wind down the night before.
                </p>
                <p>
                  By giving your eyes and mind a short, warm break from stimulation, many people say they fall asleep feeling more settled.
                </p>
                <p>
                  The result? Mornings that feel a little less harsh, and a body that doesn't feel like it fought for every minute of rest.
                </p>
              </div>
            </CardContent>
            <div className="aspect-square md:aspect-auto order-1 md:order-2">
              <img
                src="/images/WO_lady_KickinOn-Steam-Eye-Mask---Dry-Eyes-and-Eye-Strain-Relief-10.jpg"
                alt="Waking up feeling more rested, from tired and strained dry eyes"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </Card>

        {/* Reason 7 */}
        <Card className="mb-12 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-square md:aspect-auto">
              <img
                src="/images/reason5.jpg"
                alt="Steam eye mask for screen-tired, travel-heavy lifestyle"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                7. Built for screen-heavy days and sleep that doesn't come easy
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  Warm compresses were great when life moved slower and we weren't carrying our whole day in our eyes and shoulders.
                </p>
                <p>
                  Kickin'On was made for laptop marathons, long-haul flights, hotel rooms, and those seasons where deep, uninterrupted sleep feels rare.
                </p>
                <p>
                  If your eyes feel tired long before your to-do list does, this Japanese-inspired ritual gives them a much-needed pause — so deep sleep has a better chance to follow.
                </p>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Reviews Section */}
        <Card className="mb-12 border-green-200 shadow-xl bg-white">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-3xl font-extrabold text-green-900">
              What people are saying
            </CardTitle>
            <CardDescription className="text-green-600 text-base mt-2">
              Over 10,000+ five-star reviews from people who added Kickin'On to
              their nights & carry-ons
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.map((review, index) => (
                <Card key={index} className="border-l-4 border-l-green-500 bg-green-50/50 border-green-100">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-green-900">{review.author}</span>
                      <div className="flex text-amber-400">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-300 text-xs mb-3">
                      ✓ Verified Purchase
                    </Badge>
                    <p className="text-sm text-green-700/80 leading-relaxed">
                      "{review.text}"
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Timeline Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-green-900 mb-4">
            Your Kickin'On Evening & Travel Ritual Timeline
          </h2>
          <p className="text-green-600 mb-8">
            Most people feel a shift in how they wind down once they turn this into a tiny nightly (and travel day) habit 👇
          </p>

          <div className="space-y-4">
            {timelineData.map((item, index) => (
              <Card key={index} className="border-green-200 bg-green-50 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-green-600"></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-900 mb-2">{item.period}</h3>
                    <p className="text-green-700/80">{item.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button
              asChild
              size="lg"
              className="w-full max-w-xl bg-gradient-to-r from-green-700 to-green-600 hover:from-green-800 hover:to-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <a href={productUrl}>
                START YOUR EVENING & TRAVEL RITUAL
              </a>
            </Button>
            <p className="mt-4 text-green-600">
              Tiny mask. Big reset for tired eyes & busy minds.
            </p>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-green-900 mb-4">
            How Kickin'On Steam Eye Mask Stands Apart
          </h2>
          <p className="text-green-600 mb-8">
            Most "eye care" products only cool or coat. Kickin'On uses{' '}
            <strong className="text-green-800">gentle, warming steam</strong> to create a cosy wind-down moment — at home or on the move 👇
          </p>

          <Card className="border-2 border-green-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-green-50 hover:bg-green-50">
                  <TableHead className="w-[100px] text-center">
                    <div className="bg-green-100 rounded-lg p-2 inline-block">
                      <img
                        src="/images/offer-section.jpg"
                        alt="Kickin'On Icon"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </TableHead>
                  <TableHead className="text-green-900 font-semibold">Feature</TableHead>
                  <TableHead className="text-center text-green-900 font-semibold">Basic Cooling Masks & Gels</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow key={index} className="hover:bg-green-50/50">
                    <TableCell className="text-center bg-green-50">
                      <div className="inline-flex items-center justify-center w-7 h-7 rounded bg-green-500">
                        <Check className="w-5 h-5 text-white" strokeWidth={3} />
                      </div>
                    </TableCell>
                    <TableCell className="text-green-800 font-medium">{feature}</TableCell>
                    <TableCell className="text-center">
                      <X className="w-7 h-7 text-red-500 mx-auto" strokeWidth={3} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>

        {/* Final Message */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-green-900 mb-6">
            Reclaim calmer nights & easier travel with{' '}
            <span className="text-green-600">Kickin'On</span>
          </h2>

          <div className="space-y-4 text-green-700/90 mb-8">
            <p>
              If your days are full, your brain is loud, and your nights feel
              more chaotic than calm — you're not alone. Most of us were never
              taught how to actually switch off.
            </p>
            <p>
              <strong className="text-green-800">Kickin'On Steam Eye Mask</strong> gives you a tiny,
              Japanese-inspired ritual: warm steam, soft pressure, and darkness that gently mark the end of the day.
            </p>
            <p>
              No complicated routine. No apps. No pressure to "fix" yourself.
              Just one small, cosy habit you can take to bed, on flights, into
              hotel rooms, and through the seasons of life where you're running
              on fumes but still showing up.
            </p>
            <p>
              One mask at night can become your simple signal to unwind, slow down, and feel a little more like the version of you that isn't constantly running behind.
            </p>
          </div>

          {/* Photo Grid */}
          <div className="grid grid-cols-3 gap-3 max-w-2xl mx-auto mb-8">
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Eye className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Moon className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Leaf className="w-12 h-12 text-green-400" />
            </div>
            <Card className="aspect-square rounded-xl bg-gradient-to-br from-green-600 to-green-700 border-0 flex items-center justify-center">
              <CardContent className="p-4 text-center text-white">
                <div className="text-xs mb-1">★ ★ ★ ★ ★</div>
                <div className="text-[10px] font-bold tracking-wide">RISK-FREE</div>
                <div className="text-2xl font-extrabold">30-DAY</div>
                <div className="text-[10px] font-semibold">TRY IT OUT</div>
                <div className="text-[10px] mt-1 opacity-90">rated 4.7/5</div>
              </CardContent>
            </Card>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Heart className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Shield className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Star className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-green-400" />
            </div>
          </div>

          {/* Benefits List */}
          <ul className="space-y-3 mb-8">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-green-800 font-medium">
                <div className="flex-shrink-0 w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                {benefit}
              </li>
            ))}
          </ul>

          <p className="text-lg font-semibold text-green-800 text-center mb-6">
            Your calm matters. Your rest matters. And your eyes deserve a soft
            landing at the end of every day — and every flight.
          </p>

          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-green-700 to-green-600 hover:from-green-800 hover:to-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <a href={productUrl}>
                TRY KICKIN'ON STEAM EYE MASK RISK-FREE
              </a>
            </Button>
            <p className="mt-4 text-green-600">
              Still here. Still tired. Still Kickin'On.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <Card className="mb-12 bg-gradient-to-r from-green-700 to-green-600 border-0 shadow-2xl overflow-hidden">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="flex justify-center">
                <img
                  src="/images/offer-section.jpg"
                  alt="Kickin'On Steam Eye Mask - Buy 2 Get 1 Free"
                  className="w-full max-w-sm rounded-xl shadow-xl"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-extrabold text-white mb-2">
                  👉 Start Your Evening & Travel Ritual
                </h3>
                <p className="text-white/90 text-lg font-semibold mb-4">
                  Kickin'On Steam Eye Mask — The Japanese-Inspired Wind-Down
                  Ritual
                </p>
                <Badge className="bg-white/20 text-white border-white/30 text-sm font-bold mb-6">
                  Buy 2 Get 1 FREE — Limited Time Only
                </Badge>
                <div>
                  <Button
                    asChild
                    size="lg"
                    className="bg-white text-green-700 hover:bg-green-50 font-bold shadow-lg hover:shadow-xl transition-all w-full md:w-auto"
                  >
                    <a
                      href={productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      GET YOUR STEAM MASK BUNDLE
                    </a>
                  </Button>
                </div>
                <p className="text-white/80 text-sm mt-4">
                  Tiny mask. Big reset for nights, flights & everything in
                  between.
                </p>
                <p className="text-white font-medium text-sm mt-2">
                  30-Day Risk-Free Trial
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-green-900 text-center mb-8">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="space-y-3">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-2 border-green-200 rounded-xl overflow-hidden bg-white hover:border-green-400 transition-colors"
              >
                <AccordionTrigger className="px-6 py-4 text-left text-green-900 font-semibold hover:text-green-700 hover:no-underline [&[data-state=open]>svg]:text-green-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-green-700/80">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DeepSleep;
